import BaseButton from '@/components/base/BaseButton';
import BaseEllipsisText from '@/components/base/BaseEllipsisText';
import { useIntl } from '@/i18n';
import { useAuthStore } from '@/store/auth';
import { LeftOutlined, MenuOutlined, RightOutlined } from '@ant-design/icons';
import { Layout } from 'antd';
import { Link } from 'react-router';
import Breadcrumbs from './Breadcrumbs';
import HeaderActions from './HeaderActions';
import OrganizationLogo from './OrganizationLogo';

interface AdminHeaderProps {
  isMobile: boolean;
  moduleTitle?: string;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  menuOpen: boolean;
  onMenuOpen: () => void;
}

/** 同一条 Header 组织 Logo、导航层级与帐号入口，窄屏只保留必要操作。 */
export default function AdminHeader({
  isMobile,
  moduleTitle,
  collapsed,
  onToggleCollapsed,
  onMenuOpen,
}: AdminHeaderProps) {
  const intl = useIntl();
  const organizationName = useAuthStore((state) => state.tenantName) ?? '';
  const organization = { name: organizationName };
  const submenuCollapsed = collapsed;
  const onToggleSubmenu = onToggleCollapsed;
  const t = {
    'header.navigation': intl.formatMessage({ id: 'layout.navigation' }),
    'common.expand': intl.formatMessage({ id: 'layout.expandMenu' }),
    'common.collapse': intl.formatMessage({ id: 'layout.collapseMenu' }),
  };
  return (
    <>
      <Layout.Header className="admin-topbar h-18 flex flex-[0_0_72px] items-center p-0 leading-[20px] max-[992px]:relative border-b border-split bg-container">
        <Link
          to="/"
          className="admin-header-logo focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-primary focus-visible:outline-offset-[-3px] flex w-20 h-18 flex-[0_0_80px] items-center justify-start self-start p-0 ps-5 border-0 border-e-[1px] border-solid border-e-split bg-surface-soft cursor-pointer max-[992px]:absolute max-[992px]:start-[50%] max-[992px]:-translate-x-1/2 max-[992px]:border-e-0 max-[992px]:bg-transparent"
          aria-label={organizationName}
        >
          <OrganizationLogo {...organization} />
        </Link>
        {!isMobile && moduleTitle && (
          <div
            className={`admin-header-module h-18 overflow-hidden border-0 border-solid border-split transition-[width,flex-basis] duration-200 ${
              submenuCollapsed
                ? 'is-collapsed w-0 flex-[0_0_0px]'
                : 'w-[210px] flex-[0_0_210px] border-e'
            }`}
            aria-hidden={submenuCollapsed}
          >
            <div className="admin-submenu-title w-[210px] min-h-18 pt-6 px-4 pb-3.5 box-border text-[var(--ant-color-text-heading)] text-[15px] font-semibold whitespace-nowrap">
              <BaseEllipsisText title={moduleTitle} />
            </div>
          </div>
        )}
        {isMobile && (
          <BaseButton
            type="text"
            className="admin-mobile-menu-trigger"
            icon={<MenuOutlined />}
            aria-label={t['header.navigation']}
            onClick={onMenuOpen}
          />
        )}
        <div className="admin-header-main relative flex flex-1 min-w-0 h-full items-center gap-4 pt-0 pr-3 pb-0 pl-9 min-[992px]:max-[1101px]:ps-5 min-[992px]:max-[1101px]:gap-3 max-[992px]:px-4">
          {!isMobile && moduleTitle && (
            <BaseButton
              className="admin-submenu-toggle"
              aria-controls="admin-submenu"
              aria-label={
                t[submenuCollapsed ? 'common.expand' : 'common.collapse']
              }
              aria-expanded={!submenuCollapsed}
              icon={submenuCollapsed ? <RightOutlined /> : <LeftOutlined />}
              onClick={onToggleSubmenu}
            />
          )}
          {!isMobile && <Breadcrumbs />}
          <HeaderActions />
        </div>
      </Layout.Header>
    </>
  );
}
